import RestoreIcon from "@mui/icons-material/Restore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useGetKnowledgeBaseArticleRevisionsQuery,
  useRevertKnowledgeBaseArticleMutation,
} from "knowledge-base/_api/knowledge-base-api";
import KnowledgeBaseArticle from "knowledge-base/knowledge-base-article";
import { useState } from "react";

type Props = Readonly<{ article: KnowledgeBaseArticle }>;

export default function ArticleHistory({ article }: Props) {
  const { data: revisions, isLoading } = useGetKnowledgeBaseArticleRevisionsQuery(article.id);
  const [revert, { isLoading: isReverting }] = useRevertKnowledgeBaseArticleMutation();
  const [confirming, setConfirming] = useState<number | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!revisions || revisions.length === 0) {
    return <Alert severity="info">This article has no recorded history.</Alert>;
  }

  const newestFirst = [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber);
  const current = newestFirst[0].revisionNumber;

  const confirmRevert = async () => {
    if (confirming === undefined) return;

    setFailure(undefined);
    const outcome = await revert({ articleId: article.id, revisionNumber: confirming });

    // A mutation resolves with an error result rather than throwing, so closing unconditionally
    // told the author the wording had been put back when the server had refused to put it back.
    if ("error" in outcome) {
      setFailure(messageFor(outcome.error));
      return;
    }

    setConfirming(undefined);
  };

  return (
    <>
      <Stack spacing={2}>
        {newestFirst.map((revision) => (
          <Paper
            elevation={0}
            key={revision.revisionNumber}
            sx={{ border: 1, borderColor: "divider", p: 2 }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
              <Chip label={`r${revision.revisionNumber}`} size="small" />
              <Chip label={revision.kind} size="small" variant="outlined" />
              {revision.revisionNumber === current && (
                <Chip color="primary" label="Current" size="small" />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Typography color="text.secondary" variant="caption">
                {new Date(revision.timeStamp).toLocaleString()}
              </Typography>
            </Stack>

            <Typography gutterBottom variant="body2">
              {revision.note}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
              }}
              variant="caption"
            >
              {revision.body}
            </Typography>

            {revision.revisionNumber !== current && (
              <Button
                disabled={isReverting}
                onClick={() => setConfirming(revision.revisionNumber)}
                size="small"
                startIcon={<RestoreIcon />}
                sx={{ mt: 1 }}
              >
                Restore this version
              </Button>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog
        onClose={() => {
          setConfirming(undefined);
          setFailure(undefined);
        }}
        open={confirming !== undefined}
      >
        <DialogTitle>Restore r{confirming}?</DialogTitle>
        <DialogContent>
          {failure && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {failure}
            </Alert>
          )}
          <DialogContentText>
            {article.isFormLetter
              ? "This puts the earlier wording back, and the next recipient of this letter will read it. Nothing is lost — the restore is recorded as its own revision."
              : "This puts the earlier wording back. Nothing is lost — the restore is recorded as its own revision."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirming(undefined);
              setFailure(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={isReverting}
            onClick={confirmRevert}
            startIcon={<RestoreIcon />}
            variant="contained"
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * The server's own words where it gave any, and a plain sentence where it did not.
 */
function messageFor(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === "string" && data.trim().length > 0) return data;
  }

  return "That version could not be restored. Please try again.";
}
